import React from 'react';
import {
    CreditCard,
    RefreshCw,
    ArrowUpCircle,
    XCircle,
    Database,
    ShoppingBag,
    ShieldAlert,
    Scale,
    Calendar,
    Infinity,
    Zap,
    Archive
} from 'lucide-react';
import { Card, Heading, Text, Badge } from './ui';

export default function CreditGuide() {
    return (
        <div className="w-full space-y-12">

            {/* Section 1: Subscription Lifecycle */}
            <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 rounded-lg bg-[#7B61FF]/20 text-[#BA87FF]">
                        <RefreshCw size={24} />
                    </div>
                    <Heading level={3} className="text-white">The Subscription Lifecycle</Heading>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
                    {/* Connecting Line (Desktop) */}
                    <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-[#7B61FF]/0 via-[#7B61FF]/30 to-[#7B61FF]/0 -translate-y-1/2 z-0" />

                    {/* Step 1 */}
                    <Card className="relative z-10 bg-[#0A0A0A]/80 backdrop-blur-xl border-white/10 p-6 flex flex-col items-center text-center space-y-4 hover:border-[#7B61FF]/50 transition-colors">
                        <div className="w-16 h-16 rounded-full bg-[#7B61FF]/10 flex items-center justify-center border border-[#7B61FF]/20 text-[#BA87FF]">
                            <CreditCard size={32} />
                        </div>
                        <div>
                            <Text variant="primary" weight="bold" className="mb-2">New Subscription</Text>
                            <Text variant="secondary" size="sm">
                                Get credits instantly upon signup. They reset each month and do not roll over.
                            </Text>
                        </div>
                    </Card>

                    {/* Step 2 */}
                    <Card className="relative z-10 bg-[#0A0A0A]/80 backdrop-blur-xl border-white/10 p-6 flex flex-col items-center text-center space-y-4 hover:border-[#7B61FF]/50 transition-colors">
                        <div className="w-16 h-16 rounded-full bg-[#7B61FF]/10 flex items-center justify-center border border-[#7B61FF]/20 text-[#BA87FF]">
                            <ArrowUpCircle size={32} />
                        </div>
                        <div>
                            <Text variant="primary" weight="bold" className="mb-2">Upgrading Plan</Text>
                            <Text variant="secondary" size="sm">
                                Pay a prorated amount and instantly receive the additional credits for your new tier.
                            </Text>
                        </div>
                    </Card>

                    {/* Step 3 */}
                    <Card className="relative z-10 bg-[#0A0A0A]/80 backdrop-blur-xl border-white/10 p-6 flex flex-col items-center text-center space-y-4 hover:border-[#7B61FF]/50 transition-colors">
                        <div className="w-16 h-16 rounded-full bg-[#7B61FF]/10 flex items-center justify-center border border-[#7B61FF]/20 text-[#BA87FF]">
                            <XCircle size={32} />
                        </div>
                        <div>
                            <Text variant="primary" weight="bold" className="mb-2">Cancellation</Text>
                            <Text variant="secondary" size="sm">
                                Access continues until your paid period ends, when subscription credits expire.
                            </Text>
                        </div>
                    </Card>
                </div>
            </div>

            {/* Section 2: Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

                {/* Rules & Policies */}
                <Card className="p-8 border-white/10 bg-gradient-to-br from-white/5 to-transparent backdrop-blur-md">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 rounded-lg bg-orange-500/20 text-orange-400">
                            <Scale size={24} />
                        </div>
                        <Heading level={3} className="text-white">Credit Rules & Policies</Heading>
                    </div>

                    <div className="space-y-6">
                        <div className="flex items-start gap-4">
                            <div className="mt-1 p-2 bg-blue-500/10 rounded-full text-blue-400">
                                <Zap size={20} />
                            </div>
                            <div>
                                <Text variant="primary" weight="bold" className="mb-1">Spending Order</Text>
                                <Text variant="secondary" size="sm">
                                    The system always uses your monthly <span className="text-white font-medium">Subscription Credits</span> first before touching any <span className="text-white font-medium">Purchased Credits</span>.
                                </Text>
                            </div>
                        </div>

                        <div className="w-full h-px bg-white/10" />

                        <div className="flex items-start gap-4">
                            <div className="mt-1 p-2 bg-red-500/10 rounded-full text-red-400">
                                <ShieldAlert size={20} />
                            </div>
                            <div>
                                <Text variant="primary" weight="bold" className="mb-1">Refund Policy</Text>
                                <Text variant="secondary" size="sm">
                                    All purchases are final. Refunds are not automatic; disputes are handled on a case-by-case basis.
                                </Text>
                            </div>
                        </div>
                    </div>
                </Card>

                {/* Comparison Table */}
                <Card className="p-8 border-white/10 bg-gradient-to-br from-white/5 to-transparent backdrop-blur-md overflow-hidden">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 rounded-lg bg-green-500/20 text-green-400">
                            <Database size={24} />
                        </div>
                        <Heading level={3} className="text-white">Credit Types</Heading>
                    </div>

                    <div className="border border-white/10 rounded-xl overflow-hidden">
                        {/* Header */}
                        <div className="grid grid-cols-3 bg-white/5 border-b border-white/10 py-3 px-4">
                            <div className="col-span-1"></div>
                            <div className="col-span-1 text-center font-bold text-[#BA87FF] text-sm tracking-wider uppercase">Subscription</div>
                            <div className="col-span-1 text-center font-bold text-orange-400 text-sm tracking-wider uppercase">Purchased</div>
                        </div>

                        {/* Row 1: Source */}
                        <div className="grid grid-cols-3 border-b border-white/5 py-4 px-4 hover:bg-white/5 transition-colors">
                            <div className="col-span-1 flex items-center gap-2">
                                <Database size={16} className="text-gray-400" />
                                <Text variant="secondary" size="sm">Source</Text>
                            </div>
                            <div className="col-span-1 text-center border-l border-white/5">
                                <Badge variant="default" className="bg-[#7B61FF]/20 text-[#BA87FF] border-[#7B61FF]/30">Monthly Plan</Badge>
                            </div>
                            <div className="col-span-1 text-center border-l border-white/5">
                                <Badge variant="default" className="bg-orange-500/20 text-orange-400 border-orange-500/30">One-time Buy</Badge>
                            </div>
                        </div>

                        {/* Row 2: Expires */}
                        <div className="grid grid-cols-3 border-b border-white/5 py-4 px-4 hover:bg-white/5 transition-colors">
                            <div className="col-span-1 flex items-center gap-2">
                                <Calendar size={16} className="text-gray-400" />
                                <Text variant="secondary" size="sm">Expires?</Text>
                            </div>
                            <div className="col-span-1 text-center border-l border-white/5">
                                <Text variant="primary" size="sm">Resets Monthly</Text>
                            </div>
                            <div className="col-span-1 text-center border-l border-white/5">
                                <div className="flex items-center justify-center gap-1 text-white">
                                    <Infinity size={16} />
                                    <span className="text-sm">Never</span>
                                </div>
                            </div>
                        </div>

                        {/* Row 3: On Cancellation */}
                        <div className="grid grid-cols-3 py-4 px-4 hover:bg-white/5 transition-colors">
                            <div className="col-span-1 flex items-center gap-2">
                                <Archive size={16} className="text-gray-400" />
                                <Text variant="secondary" size="sm">On Cancel</Text>
                            </div>
                            <div className="col-span-1 text-center border-l border-white/5">
                                <Text variant="primary" size="sm" className="text-red-300">Expires at end</Text>
                            </div>
                            <div className="col-span-1 text-center border-l border-white/5">
                                <Text variant="primary" size="sm" className="text-green-300">Kept Forever</Text>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}
